import type { QueryClient, QueryKey } from "@tanstack/react-query";

const Modules = ["team", "people", "home", "shell", "analytics", "admin-settings", "account-settings", "organization", "goals", "reviews"] as const;
type Module = (typeof Modules)[number];

const Features = [
    "members",
    "good-vibes",
    "feedback",
    "feedback-inbox",
    "goals",
    "reviews",
    "engagement",
    "departments",
    "job-titles",
    "locations",
    "workspaces",
    "session",
    "analytics",
    "review-themes",
    "review-cycles",
    "agent-features",
    "feature-access",
    "agent-skills",
    "knowledge-sources",
    "connected-apps",
    "job-levels",
    "meetings",
    "company",
    "segments",
    "manager-impact",
    "org-chart",
    "jobs",
    "provisioning",
    "job-functions"
] as const;
type Feature = (typeof Features)[number];

export function queryPath(module: Module, reads: readonly Feature[]) {
    return [module, [...new Set(reads)].toSorted().join(",")];
}

interface InvalidateOptions {
    except?: readonly QueryKey[];
}

export function invalidate(queryClient: QueryClient, features: Feature | readonly Feature[], options: InvalidateOptions = {}) {
    const wanted = typeof features === "string" ? [features] : features;
    const exceptPaths = (options.except ?? []).map(key => key[0]);

    return queryClient.invalidateQueries({
        predicate: query => {
            const path = query.queryKey[0];
            if (!Array.isArray(path)) {
                return false;
            }

            const reads = typeof path[1] === "string" ? path[1].split(",") : [];
            if (!wanted.some(feature => reads.includes(feature))) {
                return false;
            }

            return !exceptPaths.some(exceptPath => Array.isArray(exceptPath) && exceptPath.every((segment, index) => segment === path[index]));
        }
    });
}
